import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["item", "issueModal", "issueNotes", "issueItemName", "providersContainer", "providersList"]
  static values = {
    coachId: Number,
    inspectionId: Number
  }

  currentResultId = null
  currentCategory = null

  connect() {
    // Add animation classes when connected
    this.itemTargets.forEach((item, index) => {
      item.style.animationDelay = `${index * 0.05}s`
    })
  }

  // Quick OK action with animation
  markOk(event) {
    event.preventDefault()
    const button = event.currentTarget
    const item = button.closest('[data-pretrip-checklist-target="item"]')
    const resultId = item.dataset.resultId

    // Add checking animation
    item.classList.add('checking')
    button.classList.add('pulse-success')

    this.submitCheck(resultId, 'ok', null, item, button)
  }

  // Quick Skip action
  markSkipped(event) {
    event.preventDefault()
    const button = event.currentTarget
    const item = button.closest('[data-pretrip-checklist-target="item"]')
    const resultId = item.dataset.resultId

    item.classList.add('checking')
    this.submitCheck(resultId, 'skipped', null, item, button)
  }

  // Reset item back to unchecked
  resetItem(event) {
    event.preventDefault()
    const button = event.currentTarget
    const item = button.closest('[data-pretrip-checklist-target="item"]')
    const resultId = item.dataset.resultId

    item.classList.add('checking')
    this.submitCheck(resultId, 'unchecked', null, item, button)
  }

  // Open issue modal for notes and service providers
  openIssueModal(event) {
    event.preventDefault()
    const button = event.currentTarget
    const item = button.closest('[data-pretrip-checklist-target="item"]')

    this.currentResultId = item.dataset.resultId
    this.currentCategory = item.dataset.category
    const itemName = item.querySelector('.item-name').textContent

    // Set modal content
    this.issueItemNameTarget.textContent = itemName
    this.issueNotesTarget.value = ''

    // Show modal
    this.issueModalTarget.classList.add('active')
    document.body.classList.add('modal-open')

    // Load nearby service providers
    this.loadProviders()
  }

  closeIssueModal() {
    this.issueModalTarget.classList.remove('active')
    document.body.classList.remove('modal-open')
    this.currentResultId = null
    this.currentCategory = null
  }

  // Submit issue with notes
  submitIssue(event) {
    event.preventDefault()
    const notes = this.issueNotesTarget.value

    if (!this.currentResultId) return

    const item = document.querySelector(`[data-result-id="${this.currentResultId}"]`)
    item.classList.add('checking')

    this.submitCheck(this.currentResultId, 'issue', notes, item, null)
    this.closeIssueModal()
  }

  // Load nearby service providers based on category
  async loadProviders() {
    this.providersContainerTarget.classList.add('loading')

    try {
      const response = await fetch(`/coaches/${this.coachIdValue}/pre_trip/providers?category=${this.currentCategory}`)
      const data = await response.json()

      this.renderProviders(data.providers)
    } catch (error) {
      console.error('Failed to load providers:', error)
      this.providersListTarget.innerHTML = '<p class="error-message">Unable to load service providers</p>'
    } finally {
      this.providersContainerTarget.classList.remove('loading')
    }
  }

  renderProviders(providers) {
    if (!providers || providers.length === 0) {
      this.providersListTarget.innerHTML = `
        <div class="no-providers">
          <i class="fas fa-info-circle"></i>
          <p>No service providers found nearby</p>
          <span>Try expanding your search or contact our support</span>
        </div>
      `
      return
    }

    const html = providers.map(provider => {
      const name = this.escapeHtml(provider.name)
      const avatarUrl = this.safeUrl(provider.avatar_url)
      const initial = this.escapeHtml((provider.name || "").charAt(0))
      const specialties = this.escapeHtml(provider.specialties)
      const phoneHref = this.safeUrl(`tel:${String(provider.phone || "").replace(/[^0-9+().\- ]/g, "")}`)
      return `
      <div class="provider-card ${provider.tier}" data-provider-id="${provider.id}">
        ${provider.tier === 'platinum' || provider.tier === 'gold' ? '<span class="featured-badge"><i class="fas fa-star"></i> Featured</span>' : ''}
        <div class="provider-header">
          <div class="provider-avatar">
            ${avatarUrl ? `<img src="${avatarUrl}" alt="${name}">` : `<span>${initial}</span>`}
          </div>
          <div class="provider-info">
            <h4>${name}</h4>
            <div class="provider-meta">
              ${provider.verified ? '<span class="verified-badge"><i class="fas fa-check-circle"></i> Verified</span>' : ''}
              <span class="distance"><i class="fas fa-map-marker-alt"></i> ${provider.distance} mi</span>
            </div>
          </div>
        </div>
        ${provider.specialties ? `<p class="provider-specialties">${specialties}</p>` : ''}
        <div class="provider-actions">
          <button class="btn-contact" data-action="click->pretrip-checklist#contactProvider" data-provider-id="${provider.id}">
            <i class="fas fa-comment"></i> Contact
          </button>
          ${provider.phone && phoneHref ? `<a href="${phoneHref}" class="btn-call"><i class="fas fa-phone"></i> Call</a>` : ''}
        </div>
      </div>
    `}).join('')

    this.providersListTarget.innerHTML = html
  }

  // Contact provider - creates a lead
  async contactProvider(event) {
    event.preventDefault()
    const button = event.currentTarget
    const providerId = button.dataset.providerId

    button.disabled = true
    button.innerHTML = '<i class="fas fa-spinner fa-spin"></i> Connecting...'

    try {
      const response = await fetch(`/coaches/${this.coachIdValue}/pre_trip/contact_provider`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({
          provider_id: providerId,
          result_id: this.currentResultId,
          inspection_id: this.inspectionIdValue,
          notes: this.issueNotesTarget.value
        })
      })

      const data = await response.json()

      if (data.success) {
        button.innerHTML = '<i class="fas fa-check"></i> Request Sent!'
        button.classList.add('success')

        // Show success message
        this.showNotification('Service request sent! The provider will contact you soon.')
      } else {
        throw new Error(data.error || 'Failed to contact provider')
      }
    } catch (error) {
      console.error('Failed to contact provider:', error)
      button.innerHTML = '<i class="fas fa-exclamation-triangle"></i> Try Again'
      button.disabled = false
    }
  }

  // Submit check to server
  async submitCheck(resultId, status, notes, item, button) {
    const url = `/coaches/${this.coachIdValue}/pre_trip/${this.inspectionIdValue}/check/${resultId}`

    try {
      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
          'Accept': 'text/vnd.turbo-stream.html'
        },
        body: JSON.stringify({ status, notes })
      })

      if (response.ok) {
        const html = await response.text()

        // Process turbo stream
        Turbo.renderStreamMessage(html)

        // Add success animation
        setTimeout(() => {
          item.classList.remove('checking')
          item.classList.add('checked')
          if (button) button.classList.remove('pulse-success')

          // Celebration effect for completing items
          this.checkProgress()
        }, 300)
      }
    } catch (error) {
      console.error('Failed to submit check:', error)
      item.classList.remove('checking')
      if (button) button.classList.remove('pulse-success')
    }
  }

  // Check progress and celebrate milestones
  checkProgress() {
    const total = this.itemTargets.length
    const checked = this.itemTargets.filter(i => i.classList.contains('checked') || i.dataset.status !== 'unchecked').length
    const percentage = Math.round((checked / total) * 100)

    // Check if current category is complete
    this.checkCategoryCompletion()

    if (percentage === 100) {
      this.showNotification('All items checked! Ready to complete inspection.', 'success')
    } else if (percentage === 50) {
      this.showNotification('Halfway there! Keep going.', 'info')
    }
  }

  // Check if current category is complete and auto-advance
  checkCategoryCompletion() {
    const activeSection = document.querySelector('.checklist-section.active')
    if (!activeSection) return

    const currentCategory = activeSection.dataset.category
    const categoryItems = activeSection.querySelectorAll('[data-pretrip-checklist-target="item"]')
    const uncheckedItems = Array.from(categoryItems).filter(item => item.dataset.status === 'unchecked')

    // If all items in category are checked, advance to next
    if (uncheckedItems.length === 0) {
      const categoryLabel = this.getCategoryLabel(currentCategory)
      this.showNotification(`${categoryLabel} complete!`, 'success')

      // Wait a moment then advance to next category
      setTimeout(() => this.advanceToNextCategory(currentCategory), 800)
    }
  }

  // Get the display label for a category
  getCategoryLabel(category) {
    const tab = document.querySelector(`.category-tab[data-category="${category}"] .tab-label`)
    return tab ? tab.textContent : category
  }

  // Advance to the next incomplete category
  advanceToNextCategory(currentCategory) {
    const tabs = Array.from(document.querySelectorAll('.category-tab'))
    const currentIndex = tabs.findIndex(tab => tab.dataset.category === currentCategory)

    // Find next incomplete category
    for (let i = 1; i <= tabs.length; i++) {
      const nextIndex = (currentIndex + i) % tabs.length
      const nextTab = tabs[nextIndex]

      // Check if this tab has incomplete items (no checkmark)
      if (!nextTab.querySelector('.tab-complete')) {
        // Switch to this category
        this.switchToCategory(nextTab.dataset.category)

        // Scroll the tab into view
        nextTab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
        return
      }
    }

    // All categories complete - scroll to complete button
    const completeSection = document.getElementById('complete_section')
    if (completeSection) {
      completeSection.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }

  // Switch to a specific category (can be called with event or category string)
  switchToCategory(eventOrCategory) {
    let category
    if (typeof eventOrCategory === 'string') {
      category = eventOrCategory
    } else {
      // Called from click event with param
      eventOrCategory.preventDefault()
      category = eventOrCategory.params?.category || eventOrCategory.currentTarget?.dataset?.category
    }

    if (!category) return

    // Update tabs
    document.querySelectorAll('.category-tab').forEach(tab => {
      tab.classList.toggle('active', tab.dataset.category === category)
    })

    // Update sections
    document.querySelectorAll('.checklist-section').forEach(section => {
      section.classList.toggle('active', section.dataset.category === category)
    })
  }

  showNotification(message, type = 'success') {
    const notification = document.createElement('div')
    notification.className = `pretrip-notification ${type}`
    notification.innerHTML = `
      <i class="fas ${type === 'success' ? 'fa-check-circle' : 'fa-info-circle'}"></i>
      <span>${message}</span>
    `
    document.body.appendChild(notification)

    setTimeout(() => notification.classList.add('show'), 10)
    setTimeout(() => {
      notification.classList.remove('show')
      setTimeout(() => notification.remove(), 300)
    }, 3000)
  }

  // Handle escape key to close modal
  handleKeydown(event) {
    if (event.key === 'Escape' && this.issueModalTarget.classList.contains('active')) {
      this.closeIssueModal()
    }
  }

  // Escape for safe interpolation into text and double-quoted attributes.
  escapeHtml(text) {
    return String(text == null ? "" : text).replace(/[&<>"']/g, (c) => (
      { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]
    ))
  }

  // Allow only safe http(s)/tel/mailto/relative URLs; reject javascript: etc.
  safeUrl(url) {
    if (!url) return ""
    const raw = String(url).trim()
    if (/^(https?:\/\/|tel:|mailto:|\/)/i.test(raw)) {
      return raw.replace(/"/g, "%22").replace(/</g, "%3C").replace(/>/g, "%3E")
    }
    return ""
  }
}
